import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Send, Plus, Users, Search, Image as ImageIcon, Paperclip, Mic, MapPin,
  AlertTriangle, X, ArrowLeft, StopCircle, Download, MessageCircle, UserPlus,
} from "lucide-react";
import { api, API, apiError, authToken } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { KindText } from "@/components/KindK";

const mediaSrc = (url) => (url ? `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${authToken.get() || ""}` : "");
const timeStr = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function MessageBubble({ m }) {
  const mine = m.mine;
  const base = `max-w-[78%] rounded-2xl px-3.5 py-2.5 ${mine ? "bg-brand text-black rounded-br-md" : "rud-card rounded-bl-md"}`;
  if (m.kind === "sos") {
    return (
      <div className="flex justify-center my-2" data-testid="chat-msg-sos">
        <div className="max-w-[90%] rounded-xl border border-red-500/60 bg-red-500/10 text-red-300 px-4 py-3 text-sm text-center">
          <div className="flex items-center justify-center gap-2 font-semibold"><AlertTriangle size={16} /> {m.text}</div>
          {(m.lat != null && m.lng != null) && (
            <a href={`https://www.google.com/maps?q=${m.lat},${m.lng}`} target="_blank" rel="noreferrer" className="underline text-xs mt-1 inline-block">Open live location</a>
          )}
          <div className="text-[10px] opacity-70 mt-1">{m.sender_name} · {timeStr(m.created_at)}</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} my-1`} data-testid="chat-msg">
      <div className={base}>
        {!mine && <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</div>}
        {m.kind === "text" && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
        {m.kind === "image" && <img src={mediaSrc(m.media_url)} alt={m.media_name} className="rounded-lg max-w-full max-h-72 object-cover" />}
        {m.kind === "file" && (
          <a href={mediaSrc(m.media_url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm underline">
            <Download size={15} /> {m.media_name || "Download file"}
          </a>
        )}
        {m.kind === "voice" && <audio controls src={mediaSrc(m.media_url)} className="max-w-[240px]" />}
        {m.kind === "location" && (m.lat != null) && (
          <a href={`https://www.google.com/maps?q=${m.lat},${m.lng}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm underline">
            <MapPin size={15} /> Live location
          </a>
        )}
        <div className={`text-[9px] mt-1 text-right ${mine ? "text-black/60" : "opacity-60"}`}>{timeStr(m.created_at)}</div>
      </div>
    </div>
  );
}

export default function Chat() {
  const { user } = useApp();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [dmId, setDmId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupMembers, setGroupMembers] = useState("");
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const recStart = useRef(0);
  const scrollRef = useRef(null);
  const lastAtRef = useRef("");

  const loadThreads = useCallback(async () => {
    try { const { data } = await api.get("/chat/threads"); setThreads(data.threads || []); } catch { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async (threadId, incremental) => {
    if (!threadId) return;
    try {
      const params = incremental && lastAtRef.current ? `?after=${encodeURIComponent(lastAtRef.current)}` : "";
      const { data } = await api.get(`/chat/threads/${threadId}/messages${params}`);
      if (incremental) {
        if (data.messages.length) setMessages((prev) => [...prev, ...data.messages]);
      } else {
        setMessages(data.messages);
      }
      if (data.messages.length) lastAtRef.current = data.messages[data.messages.length - 1].created_at;
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    const t = setInterval(() => { loadThreads(); if (active) loadMessages(active.id, true); }, 4000);
    return () => clearInterval(t);
  }, [active, loadThreads, loadMessages]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const openThread = async (t) => {
    setActive(t); setMessages([]); lastAtRef.current = "";
    await loadMessages(t.id, false);
    try { await api.post(`/chat/threads/${t.id}/read`); loadThreads(); } catch { /* ignore */ }
  };

  const send = async (payload) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/chat/threads/${active.id}/message`, payload);
      setMessages((prev) => [...prev, data.message]);
      lastAtRef.current = data.message.created_at;
      loadThreads();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const sendText = async () => { if (!text.trim()) return; const t = text; setText(""); await send({ kind: "text", text: t }); };

  const uploadAndSend = async (file, kind, duration = 0) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await send({ kind, text: "", media_url: data.url, media_name: data.name, content_type: data.content_type, duration });
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const onPickImage = (e) => { const f = e.target.files?.[0]; if (f) uploadAndSend(f, "image"); e.target.value = ""; };
  const onPickFile = (e) => { const f = e.target.files?.[0]; if (f) uploadAndSend(f, "file"); e.target.value = ""; };

  const toggleVoice = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = []; recStart.current = Date.now();
      mr.ondataavailable = (ev) => chunksRef.current.push(ev.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const dur = Math.round((Date.now() - recStart.current) / 1000);
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        uploadAndSend(new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" }), "voice", dur);
      };
      recRef.current = mr; mr.start(); setRecording(true);
    } catch { toast.error("Microphone permission needed for voice notes."); }
  };

  const shareLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not supported.");
    navigator.geolocation.getCurrentPosition(
      (p) => send({ kind: "location", lat: p.coords.latitude, lng: p.coords.longitude }),
      () => toast.error("Location permission denied."));
  };

  const sendSOS = () => {
    if (!active) return;
    if (!window.confirm("Send an emergency SOS with your live location to everyone in this chat?")) return;
    const post = (lat, lng) => api.post(`/chat/threads/${active.id}/sos`, { lat, lng })
      .then(({ data }) => { setMessages((p) => [...p, data.message]); toast.success("SOS sent with your location"); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
    if (navigator.geolocation) navigator.geolocation.getCurrentPosition((p) => post(p.coords.latitude, p.coords.longitude), () => post(null, null));
    else post(null, null);
  };

  const startDM = async () => {
    if (!dmId.trim()) return;
    try {
      const { data } = await api.post("/chat/dm", { recipient: dmId.trim() });
      setDmId(""); setNewOpen(false); await loadThreads(); openThread(data.thread);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createGroup = async () => {
    if (!groupName.trim()) return;
    try {
      const { data } = await api.post("/chat/group", { name: groupName.trim(), member_ids: [] });
      const idents = groupMembers.split(",").map((s) => s.trim()).filter(Boolean);
      if (idents.length) await api.post(`/chat/group/${data.thread.id}/members`, { recipients: idents });
      setGroupName(""); setGroupMembers(""); setNewOpen(false); await loadThreads(); openThread(data.thread);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (user === null) {
    return <div className="max-w-md mx-auto px-5 pt-32 text-center"><MessageCircle className="mx-auto text-brand mb-4" /><p className="text-sec">Sign in to use <KindText text="Kind Social" /> Chat.</p></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-24 pb-6" data-testid="chat-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-3xl tracking-tight flex items-center gap-2"><MessageCircle className="text-brand" /> <KindText text="Kind" /> Chat</h1>
        <button data-testid="chat-new-btn" onClick={() => setNewOpen(true)} className="btn-primary rounded-full h-10 px-4 flex items-center gap-2 text-sm"><Plus size={16} /> New chat</button>
      </div>

      <div className="grid md:grid-cols-[320px_1fr] gap-4 h-[70vh]">
        {/* Threads */}
        <div className={`rud-card rounded-2xl overflow-hidden flex flex-col ${active ? "hidden md:flex" : "flex"}`} data-testid="chat-thread-list">
          <div className="p-3 border-b border-rud text-xs uppercase tracking-widest text-sec">Chats</div>
          <div className="flex-1 overflow-y-auto">
            {threads.length === 0 && <div className="p-6 text-center text-sec text-sm">No chats yet. Start one!</div>}
            {threads.map((t) => (
              <button key={t.id} data-testid={`chat-thread-${t.id}`} onClick={() => openThread(t)}
                className={`w-full text-left px-4 py-3 border-b border-rud/50 hover:bg-white/5 transition-colors flex items-center gap-3 ${active?.id === t.id ? "bg-white/5" : ""}`}>
                <div className="w-10 h-10 rounded-full bg-elevated border border-rud flex items-center justify-center shrink-0 overflow-hidden">
                  {t.picture ? <img src={t.picture} alt="" className="w-full h-full object-cover" /> : (t.type === "group" ? <Users size={16} className="text-brand" /> : <span className="text-sm">{(t.name || "?")[0]}</span>)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{t.name}</span>
                    {t.unread > 0 && <span data-testid="chat-unread-badge" className="bg-brand text-black text-[10px] rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{t.unread}</span>}
                  </div>
                  <div className="text-xs text-sec truncate">{t.is_sos ? "🆘 SOS active" : t.last_message || "New chat"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation */}
        <div className={`rud-card rounded-2xl overflow-hidden flex flex-col ${active ? "flex" : "hidden md:flex"}`} data-testid="chat-conversation">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sec text-sm">Select a chat to start messaging</div>
          ) : (
            <>
              <div className="p-3 border-b border-rud flex items-center gap-3">
                <button className="md:hidden" onClick={() => setActive(null)} data-testid="chat-back"><ArrowLeft size={18} /></button>
                <div className="w-9 h-9 rounded-full bg-elevated border border-rud flex items-center justify-center overflow-hidden">
                  {active.type === "group" ? <Users size={15} className="text-brand" /> : <span className="text-sm">{(active.name || "?")[0]}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{active.name}</div>
                  <div className="text-[11px] text-sec">{active.type === "group" ? `${active.members_count} members` : "Direct message"}</div>
                </div>
                <button data-testid="chat-sos-btn" onClick={sendSOS} className="flex items-center gap-1.5 text-xs rounded-full border border-red-500/60 text-red-400 px-3 h-8 hover:bg-red-500/10">
                  <AlertTriangle size={14} /> SOS
                </button>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
                {messages.map((m) => <MessageBubble key={m.id} m={m} />)}
              </div>

              <div className="p-3 border-t border-rud flex items-center gap-1.5">
                <label className="cursor-pointer p-2 rounded-lg hover:bg-white/5" title="Photo" data-testid="chat-attach-image">
                  <ImageIcon size={18} className="text-sec" /><input type="file" accept="image/*" hidden onChange={onPickImage} />
                </label>
                <label className="cursor-pointer p-2 rounded-lg hover:bg-white/5" title="File" data-testid="chat-attach-file">
                  <Paperclip size={18} className="text-sec" /><input type="file" hidden onChange={onPickFile} />
                </label>
                <button onClick={shareLocation} className="p-2 rounded-lg hover:bg-white/5" title="Live location" data-testid="chat-location"><MapPin size={18} className="text-sec" /></button>
                <button onClick={toggleVoice} className={`p-2 rounded-lg hover:bg-white/5 ${recording ? "text-red-400 animate-pulse" : "text-sec"}`} title="Voice note" data-testid="chat-voice">
                  {recording ? <StopCircle size={18} /> : <Mic size={18} />}
                </button>
                <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendText()}
                  placeholder={uploading ? "Uploading…" : recording ? "Recording… tap ■ to send" : "Type a message"}
                  className="rud-input flex-1 rounded-full px-4 py-2.5 text-sm" />
                <button data-testid="chat-send" onClick={sendText} className="btn-primary rounded-full w-10 h-10 flex items-center justify-center"><Send size={16} /></button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New chat modal */}
      {newOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setNewOpen(false)}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()}
            className="rud-card rounded-2xl p-6 w-full max-w-md" data-testid="chat-new-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-serif text-2xl">Start a chat</h3><button onClick={() => setNewOpen(false)}><X size={18} /></button></div>
            <div className="space-y-2 mb-5">
              <label className="text-xs uppercase tracking-widest text-sec flex items-center gap-1.5"><UserPlus size={13} /> Direct message</label>
              <div className="flex gap-2">
                <input data-testid="chat-dm-input" value={dmId} onChange={(e) => setDmId(e.target.value)} placeholder="Member email, phone or referral code" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
                <button data-testid="chat-dm-start" onClick={startDM} className="btn-primary px-4 rounded-lg text-sm">Chat</button>
              </div>
            </div>
            <div className="space-y-2 border-t border-rud pt-4">
              <label className="text-xs uppercase tracking-widest text-sec flex items-center gap-1.5"><Users size={13} /> New group</label>
              <input data-testid="chat-group-name" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
              <input data-testid="chat-group-members" value={groupMembers} onChange={(e) => setGroupMembers(e.target.value)} placeholder="Add members (emails/phones, comma-separated)" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
              <button data-testid="chat-group-create" onClick={createGroup} className="btn-primary w-full h-10 rounded-lg text-sm flex items-center justify-center gap-2"><Plus size={15} /> Create group</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
